import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    EditButton,
    DeleteButton,
    BulkDeleteButton,
    Filter,
    TextInput,
    SelectInput,
    ReferenceInput,
    ReferenceField,
    ShowButton,
    TopToolbar,
    CreateButton,
    ExportButton,
    Create,
    Edit,
    Show,
    SimpleForm,
    SimpleShowLayout,
    DateInput,
    BooleanInput,
    BooleanField,
    FunctionField,
    required
} from 'react-admin';
import { useMediaQuery, Box, Typography } from '@material-ui/core';
import { TimeInput } from './picker';
import AccidentDetailsInput from './AccidentDetailsInput';
import { showNotification } from 'react-admin';

const AccidentReportFilter = (props) => {
    return (
        <Filter {...props}>
            <ReferenceInput 
                label="Student Name" 
                source="SID" 
                reference="Students"
                alwaysOn
            >
                <SelectInput optionText="Sname" />
            </ReferenceInput>
            <DateInput 
                label="Date of Accident" 
                source="DateOfAccident" 
                alwaysOn
            />
        </Filter>
    );
};

const AccidentReportListActions = (props) => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

export const AccidentReportList = (props) => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    
    // Get today's date in Arizona Phoenix timezone (no daylight saving time)
    const today = new Date().toLocaleDateString('en-CA', { 
        timeZone: 'America/Phoenix',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    
    // Handle errors using the same pattern as AccidentReportButton
    const handleError = (error) => {
        if (error && error.message) {
            showNotification(error.message, 'error');
        } else {
            showNotification('An error occurred while loading accident reports', 'error');
        }
    };
    
    return (
        <List 
            {...props} 
            filters={<AccidentReportFilter />}
            actions={<AccidentReportListActions />}
            sort={{ field: 'DateOfAccident', order: 'DESC' }}
            filterDefaultValues={{ DateOfAccident: today }}
            onFailure={handleError}
        >
            {isSmall ? (
                <Datagrid bulkActionButtons={<BulkDeleteButton />}>
                    <TextField label="Student" source="StudentName" />
                    <FunctionField 
                        label="Date" 
                        source="DateOfAccident" 
                        render={record => {
                            if (!record.DateOfAccident) return '';
                            // Convert YYYY-MM-DD to MM/DD/YYYY
                            const dateParts = record.DateOfAccident.split('-');
                            if (dateParts.length === 3) {
                                return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
                            }
                            return record.DateOfAccident;
                        }}
                    />
                    <FunctionField 
                        label="Time" 
                        source="TimeOfAccident" 
                        render={record => {
                            if (!record.TimeOfAccident) return '';
                            const time = new Date(`2000-01-01T${record.TimeOfAccident}`);
                            return time.toLocaleTimeString('en-US', { 
                                hour: 'numeric', 
                                minute: '2-digit',
                                hour12: true 
                            });
                        }}
                    />
                    <TextField label="Place" source="PlaceOfAccident" />
                    <BooleanField label="Phone" source="ParentInformedPhone" />
                    <BooleanField label="In Person" source="ParentInformedInPerson" />
                    <TextField label="Created By" source="CreatedBy" />
                    <ShowButton />
                    <EditButton />
                </Datagrid>
            ) : (
                <Datagrid bulkActionButtons={<BulkDeleteButton />}>
                    <TextField label="Student Name" source="StudentName" />
                    <FunctionField 
                        label="Date of Accident" 
                        source="DateOfAccident" 
                        render={record => {
                            if (!record.DateOfAccident) return '';
                            // Convert YYYY-MM-DD to MM/DD/YYYY
                            const dateParts = record.DateOfAccident.split('-');
                            if (dateParts.length === 3) {
                                return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
                            }
                            return record.DateOfAccident;
                        }}
                    />
                    <FunctionField 
                        label="Time of Accident" 
                        source="TimeOfAccident" 
                        render={record => {
                            if (!record.TimeOfAccident) return '';
                            const time = new Date(`2000-01-01T${record.TimeOfAccident}`);
                            return time.toLocaleTimeString('en-US', { 
                                hour: 'numeric', 
                                minute: '2-digit',
                                hour12: true 
                            });
                        }}
                    />
                    <TextField label="Place of Accident" source="PlaceOfAccident" />
                    <BooleanField label="Parent Informed Phone" source="ParentInformedPhone" />
                    <BooleanField label="Parent Informed In Person" source="ParentInformedInPerson" />
                    <TextField label="Created By" source="CreatedBy" />
                    <TextField label="Created Date" source="CreatedDate" />
                    <ShowButton />
                    <EditButton />
                </Datagrid>
            )}
        </List>
    );
};

export const AccidentReportCreate = (props) => (
    <Create {...props}>
        <AccidentReportCreateForm />
    </Create>
);

export const AccidentReportEdit = (props) => (
    <Edit {...props}>
        <AccidentReportForm />
    </Edit>
);

export const AccidentReportShow = (props) => (
    <Show {...props}>
        <AccidentReportShowForm />
    </Show>
);

const AccidentReportShowForm = (props) => {
    return (
        <SimpleShowLayout {...props}>
            <ReferenceField label="Student Name" source="SID" reference="Students">
                <TextField source="Sname" />
            </ReferenceField>
            <FunctionField 
                label="Date of Accident" 
                source="DateOfAccident" 
                render={record => {
                    if (!record.DateOfAccident) return '';
                    // Convert YYYY-MM-DD to MM/DD/YYYY
                    const dateParts = record.DateOfAccident.split('-');
                    if (dateParts.length === 3) {
                        return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
                    }
                    return record.DateOfAccident;
                }}
            />
            <TextField label="Time of Accident" source="TimeOfAccident" />
            <FunctionField 
                label="Place of Accident" 
                source="PlaceOfAccident" 
                render={record => record.PlaceOfAccident}
            />
            <TextField 
                label="Accident Details" 
                source="AccidentDetails" 
                component="pre"
                style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
            />
            <BooleanField label="Parent informed via telephone" source="ParentInformedPhone" />
            <BooleanField label="Parent informed in person" source="ParentInformedInPerson" />
            <TextField label="Created By" source="CreatedBy" />
            <DateField label="Created Date" source="CreatedDate" />
        </SimpleShowLayout>
    );
};

const AccidentReportCreateForm = (props) => {
    // Get today's date in Arizona Phoenix timezone (no daylight saving time)
    const today = new Date().toLocaleDateString('en-CA', { 
        timeZone: 'America/Phoenix',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    
    // Get current user name for CreatedBy field
    const userName = localStorage.getItem('userName') || 'Unknown';
    
    return (
        <SimpleForm {...props} initialValues={{ DateOfAccident: today, SendEmail: true }}>
            <Box p="1em">
                <Box display="flex">
                    <Box>
                        <Typography variant="h6" gutterBottom>Accident Report Details</Typography>
                        <Box>
                            <Box display="flex">
                                <Box>
                                    <ReferenceInput 
                                        label="Student Name" 
                                        source="SID" 
                                        reference="Students" 
                                        filter={{ FEStatus: "Active" }} 
                                        sort={{ field: 'Sname', order: 'ASC' }}
                                        validate={[required()]}
                                    >
                                        <SelectInput optionText="Sname" />
                                    </ReferenceInput>
                                </Box>
                                <Box ml="2em">
                                    <BooleanInput source="SendEmail" label="Send Email" />
                                </Box>
                            </Box>
                            <Box display="flex">
                                <Box>
                                    <DateInput 
                                        source="DateOfAccident" 
                                        label="Date of Accident" 
                                        validate={[required()]} 
                                    />
                                </Box>
                                <Box ml="2em">
                                    <TextInput source="TimeOfAccident" label="Time of Accident" type="time" validate={[required()]} />
                                </Box>
                            </Box>
                            <Box>
                                <SelectInput 
                                    source="PlaceOfAccident" 
                                    label="Place of Accident"
                                    style={{ width: "300px" }}
                                    choices={[
                                        { id: 'Classroom', name: 'Classroom' },
                                        { id: 'Playground', name: 'Playground' },
                                        { id: 'Extended Care Room', name: 'Extended Care Room' }
                                    ]}
                                    validate={[required()]}
                                />
                            </Box>
                            <Box>
                                <AccidentDetailsInput source="AccidentDetails" label="Accident Details" validate={[required()]} />
                            </Box>
                            <Box display="flex">
                                <Box>
                                    <BooleanInput source="ParentInformedPhone" label="Parent informed via telephone" />
                                </Box>
                                <Box ml="2em">
                                    <BooleanInput source="ParentInformedInPerson" label="Parent informed in person" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </SimpleForm>
    );
};

const AccidentReportForm = (props) => {
    const { record } = props;
    
    return (
        <SimpleForm {...props}>
            <Box p="1em">
                <Box display="flex">
                    <Box>
                        <Typography variant="h6" gutterBottom>Accident Report Details</Typography>
                        <Box>
                            <Box display="flex">
                                <Box>
                                    <ReferenceField 
                                        label="Student Name" 
                                        source="SID" 
                                        reference="Students"
                                    >
                                        <TextField source="Sname" />
                                    </ReferenceField>
                                </Box>
                                <Box ml="2em">
                                    <BooleanInput source="SendEmail" label="Send Email" />
                                </Box>
                            </Box>
                            <Box display="flex">
                                <Box>
                                    <DateInput 
                                        source="DateOfAccident" 
                                        label="Date of Accident" 
                                        validate={[required()]} 
                                    />
                                </Box>
                                <Box ml="2em">
                                    <TextInput source="TimeOfAccident" label="Time of Accident" type="time" validate={[required()]} />
                                </Box>
                            </Box>
                            <Box>
                                <SelectInput 
                                    source="PlaceOfAccident" 
                                    label="Place of Accident"
                                    style={{ width: "300px" }}
                                    choices={[
                                        { id: 'Classroom', name: 'Classroom' },
                                        { id: 'Playground', name: 'Playground' },
                                        { id: 'Extended Care Room', name: 'Extended Care Room' }
                                    ]}
                                    validate={[required()]}
                                />
                            </Box>
                            <Box>
                                <AccidentDetailsInput source="AccidentDetails" label="Accident Details" validate={[required()]} />
                            </Box>
                            <Box display="flex">
                                <Box>
                                    <BooleanInput source="ParentInformedPhone" label="Parent informed via telephone" />
                                </Box>
                                <Box ml="2em">
                                    <BooleanInput source="ParentInformedInPerson" label="Parent informed in person" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </SimpleForm>
    );
};
